import React, { useState, useEffect, useRef } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";
import { IconButton, InputAdornment } from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	formControl: {
		margin: theme.spacing(1),
		minWidth: 120,
	},
	colorAdorment: {
		width: 20,
		height: 20,
	},
	floatingButton: {
		transition: 'transform 0.3s',
	
		'&:hover': {
			transform: 'translateY(-5px)',
		},
	},
}));

const labelSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	color: Yup.string().min(3, "Too Short!").max(9, "Too Long!").required(),
	description: Yup.string().max(50, "Too Long!"),
});

const LabelModal = ({ open, onClose, labelId }) => {
	const classes = useStyles();

	const initialState = {
		name: "",
		color: "",
		description: "",
	};

	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [label, setLabel] = useState(initialState);
	const descriptionRef = useRef();

	useEffect(() => {
		(async () => {
			if (!labelId) return;
			try {
				const { data } = await api.get(`/label/${labelId}`);
				setLabel(prevState => {
					return { ...prevState, ...data };
				});
			} catch (error) {
				console.log("Label Modal Use Effect:", error);
				toastError(error);
			}
		})();

		return () => {
			setLabel({
				name: "",
				color: "",
				description: "",
			});
		};
	}, [labelId, open]);

	const handleClose = () => {
		onClose();
		setLabel(initialState);
	};

	const handleSaveLabel = async values => {
		try {
			if (labelId) await api.put(`/label/${labelId}`, values);
			else await api.post("/label", values);
			
			handleClose();
		} catch (error) {
			console.log("Handle Save Label Error:", error);
			toastError(error);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} scroll="paper">
				<DialogTitle>
					{labelId
						? `${i18n.t("labelModal.title.edit")}`
						: `${i18n.t("labelModal.title.add")}`}
				</DialogTitle>
				<Formik
					initialValues={label}
					enableReinitialize={true}
					validationSchema={labelSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveLabel(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values }) => (
						<Form>
							<DialogContent dividers>
								<Field
									as={TextField}
									label={i18n.t("labelModal.form.name")}
									autoFocus
									name="name"
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<Field
									as={TextField}
									label={i18n.t("labelModal.form.color")}
									name="color"
									id="color"
									onFocus={() => {
										setColorPickerModalOpen(true);
										descriptionRef.current.focus();
									}}
									error={touched.color && Boolean(errors.color)}
									helperText={touched.color && errors.color}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<div
													style={{ backgroundColor: values.color }}
													className={classes.colorAdorment}
												></div>
											</InputAdornment>
										),
										endAdornment: (
											<IconButton
												size="small"
												color="default"
												onClick={() => setColorPickerModalOpen(true)}
											>
												<Colorize />
											</IconButton>
										),
									}}
									variant="outlined"
									margin="dense"
								/>
								<ColorPicker
									open={colorPickerModalOpen}
									handleClose={() => setColorPickerModalOpen(false)}
									onChange={color => {
										values.color = color;
										setLabel(() => {
											return { ...values, color };
										});
									}}
								/>
								<div>
									<Field
										as={TextField}
										label={i18n.t("labelModal.form.description")}
										type="description"
										multiline
										inputRef={descriptionRef}
										rows={5}
										fullWidth
										name="description"
										error={
											touched.description && Boolean(errors.description)
										}
										helperText={
											touched.description && errors.description
										}
										variant="outlined"
										margin="dense"
									/>
								</div>
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="inherit"
									disabled={isSubmitting}
									variant="outlined"
									className={classes.floatingButton}
								>
									{i18n.t("labelModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={`${classes.btnWrapper} ${classes.floatingButton}`}
								>
									{labelId
										? `${i18n.t("labelModal.buttons.okEdit")}`
										: `${i18n.t("labelModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default LabelModal;
